import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface StatTileProps {
  /** Sentence case, no trailing colon. */
  label: string;
  /** Already formatted; `—` for a blank. */
  value: string;
  /** A secondary line under the value (target, period, unit). */
  sub?: ReactNode;
  /** Shown instead of `sub` when the tile has no data source (an unbound metric). */
  hint?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * One headline number (plan §2.1; dataviz "stat tile"): label, value in the primary ink, an optional secondary line.
 * Text never wears a series colour; a blank stays `—` with a hint, never 0.
 */
export const StatTile = ({ label, value, sub, hint, className, ...rest }: StatTileProps) => (
  <div
    className={cn('print-avoid-break flex min-w-0 flex-col gap-1 rounded-md border border-border bg-card px-3 py-2.5', className)}
    data-stat-tile={label}
    {...rest}
  >
    <span className="truncate text-xs text-muted-foreground">{label}</span>
    <span className="font-mono text-xl font-semibold tabular-nums text-foreground">{value}</span>
    {hint ? (
      <span className="text-[11px] italic text-muted-foreground">{hint}</span>
    ) : sub ? (
      <span className="text-[11px] text-muted-foreground">{sub}</span>
    ) : null}
  </div>
);
